import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import xIcon from "@iconify-icons/lucide/x";
import mailIcon from "@iconify-icons/lucide/mail";
import userIcon from "@iconify-icons/lucide/user";
import checkIcon from "@iconify-icons/lucide/check";

const EMAIL_RE =
  /^[-\w.%+]{1,64}@(?:[A-Z0-9-]{1,63}\.){1,125}[A-Z]{2,63}$/i;

/**
 * Gate del INVITADO — replica el flujo del `academy-master` original:
 *
 *   1. El usuario tipea el email → tras un debounce se llama a
 *      `checkEmailInBD(email)`. Si existe, se guarda `customer` en
 *      el state local del modal y se muestra un check verde.
 *   2. El usuario tipea nombre y apellido.
 *   3. Al pulsar Continuar:
 *      · Si `customer` está seteado → `handleSubmit` con
 *        `{email, name, lastName, customer}` → hook ENLAZA sin crear.
 *      · Si no → hook CREA (POST /user/user/create).
 *
 * El state vive en este componente y no en el hook `useProspect` —
 * cada consumidor del hook tenía su propia instancia y los setState
 * se pisaban con stale closures (bug reportado por el usuario).
 */
const ModalHomeProspect = ({
  isVisibleProspect,
  toggleModalProspect,
  handleSubmit,
  checkEmailInBD,
}) => {
  const { t } = useTranslation("global");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [customer, setCustomer] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isVisibleProspect) return undefined;
    setEmail("");
    setName("");
    setLastName("");
    setCustomer(null);
    setCheckingEmail(false);
    setSubmitting(false);
    const onKey = (e) => e.key === "Escape" && toggleModalProspect();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isVisibleProspect, toggleModalProspect]);

  /* Debounce del check de email — evita una petición por tecla. Se
     dispara 500 ms después de la última pulsación y solo si el
     formato parece válido. */
  useEffect(() => {
    if (!isVisibleProspect) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!EMAIL_RE.test(email)) {
      setCustomer(null);
      setCheckingEmail(false);
      return undefined;
    }
    setCheckingEmail(true);
    debounceRef.current = setTimeout(async () => {
      const found = await checkEmailInBD?.(email);
      setCustomer(found ?? null);
      setCheckingEmail(false);
      if (found) {
        if (!name && (found.name || found.firstName)) {
          setName(found.name || found.firstName);
        }
        if (!lastName && (found.lastName || found.LastName)) {
          setLastName(found.lastName || found.LastName);
        }
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isVisibleProspect]);

  if (!isVisibleProspect) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await handleSubmit(undefined, { email, name, lastName, customer });
    } catch (err) {
      console.error("[ModalHomeProspect] handleSubmit error", err);
    } finally {
      setSubmitting(false);
    }
  };

  const emailIsValid = EMAIL_RE.test(email);

  const inputStyle = {
    width: "100%",
    minHeight: 54,
    border: "1.5px solid var(--color-line-strong)",
    borderRadius: 12,
    padding: "0 44px 0 44px",
    fontSize: 16,
    fontWeight: 600,
    color: "var(--color-ink)",
    outline: "none",
    background: "#fff",
  };

  const leftIconStyle = {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--color-ink-2)",
    pointerEvents: "none",
  };

  const rightIconStyle = {
    position: "absolute",
    right: 14,
    top: "50%",
    transform: "translateY(-50%)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    pointerEvents: "none",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("prospect.title", "Antes de continuar")}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 90,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <div
        onClick={toggleModalProspect}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(11, 20, 36, 0.58)",
        }}
      />
      <div
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 20,
          padding: "26px 22px 22px",
          width: "100%",
          maxWidth: 460,
          boxShadow: "0 20px 60px rgba(11, 20, 36, 0.28)",
        }}
      >
        <button
          type="button"
          onClick={toggleModalProspect}
          aria-label={t("close", "Cerrar")}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: 0,
            background: "#F1F3F7",
            color: "var(--color-ink-2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <Icon icon={xIcon} width={17} height={17} aria-hidden="true" />
        </button>

        <h2
          style={{
            fontSize: 21,
            lineHeight: 1.28,
            fontWeight: 800,
            letterSpacing: "-.015em",
            margin: "0 0 8px",
            paddingRight: 24,
          }}
        >
          {t("prospect.title", "Antes de continuar")}
        </h2>
        <p
          style={{
            fontSize: 14.5,
            lineHeight: 1.55,
            fontWeight: 600,
            color: "#4A5568",
            margin: "0 0 20px",
          }}
        >
          {t(
            "prospect.lead",
            "Déjanos tus datos para que quien te invitó pueda acompañarte y para guardar tu progreso."
          )}
        </p>

        <form onSubmit={onSubmit}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={leftIconStyle} aria-hidden="true">
                <Icon icon={mailIcon} width={19} height={19} />
              </span>
              <input
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder={t("generalInfo.email", "Correo electrónico")}
                aria-label={t("generalInfo.email", "Correo electrónico")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
              {emailIsValid && (
                <span style={rightIconStyle} aria-hidden="true">
                  {checkingEmail ? (
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        border: "2px solid var(--color-line-strong)",
                        borderTopColor: "var(--color-ink-2)",
                        animation: "spin 700ms linear infinite",
                      }}
                    />
                  ) : customer ? (
                    <span
                      title={t(
                        "prospect.emailFound",
                        "Ya te tenemos registrado — completamos con tus datos."
                      )}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "var(--color-success)",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon icon={checkIcon} width={13} height={13} />
                    </span>
                  ) : null}
                </span>
              )}
            </div>

            {customer && (
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  fontWeight: 700,
                  color: "var(--color-success)",
                  margin: "-4px 4px 0",
                }}
              >
                {t(
                  "prospect.emailFound",
                  "Ya te tenemos registrado — completamos con tus datos."
                )}
              </div>
            )}

            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={leftIconStyle} aria-hidden="true">
                <Icon icon={userIcon} width={19} height={19} />
              </span>
              <input
                type="text"
                name="name"
                required
                autoComplete="given-name"
                placeholder={t("generalInfo.names", "Nombre(s)")}
                aria-label={t("generalInfo.names", "Nombre(s)")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={leftIconStyle} aria-hidden="true">
                <Icon icon={userIcon} width={19} height={19} />
              </span>
              <input
                type="text"
                name="lastName"
                required
                autoComplete="family-name"
                placeholder={t("generalInfo.lastname", "Apellidos")}
                aria-label={t("generalInfo.lastname", "Apellidos")}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              minHeight: 56,
              border: 0,
              borderRadius: 12,
              background: "var(--color-accent)",
              color: "var(--color-ink)",
              fontSize: 16.5,
              fontWeight: 800,
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting ? 0.7 : 1,
              marginBottom: 4,
            }}
          >
            {submitting
              ? t("prospect.saving", "Guardando…")
              : t("prospect.confirm", "Continuar")}
          </button>
        </form>

        <p
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            fontWeight: 600,
            color: "var(--color-ink-2)",
            margin: "12px 0 0",
            textAlign: "center",
          }}
        >
          {t(
            "prospect.privacy",
            "Al continuar aceptas que tus datos queden asociados a quien te invitó."
          )}
        </p>
      </div>

      {/* Keyframe local para el spinner del check email */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ModalHomeProspect;
